import { moduleRegistry } from '@/modules/moduleRegistry';

export interface ParsedEntityContext {
  source: string;
  data: Record<string, any>;
}

export interface EntityExtensionLookupConfig<TEntity, TExtension> {
  extensionPoint: string;
  parseContext: (entity: TEntity) => ParsedEntityContext | null;
  getClientId: (entity: TEntity) => string | null;
  /** Optional type guard: when provided, extension must pass this check */
  hasDisplayMethods?: (ext: unknown) => ext is TExtension;
  logPrefix?: string;
}

/**
 * Create a lookup helper for entity display extensions.
 * Encapsulates the common flow: parse entity context → check module exists → check enabled → get extension → validate.
 */
export function createEntityExtensionLookup<TEntity, TExtension>(
  config: EntityExtensionLookupConfig<TEntity, TExtension>
) {
  const prefix = config.logPrefix ?? '[Extension]';

  const getOne = (entity: TEntity): TExtension | undefined => {
    const parsed = config.parseContext(entity);
    if (!parsed) return undefined;

    if (!moduleRegistry.getDefinition(parsed.source)) {
      console.warn(`${prefix} Module definition not found for source: ${parsed.source}`);
      return undefined;
    }

    const clientId = config.getClientId(entity);
    if (!moduleRegistry.isEnabled(parsed.source, clientId)) {
      console.warn(`${prefix} Module ${parsed.source} is not enabled for client ${clientId}. Module UI extensions will not be rendered.`);
      return undefined;
    }

    const extension = moduleRegistry.getExtension<unknown>(parsed.source, config.extensionPoint);
    if (!extension) {
      console.warn(`${prefix} No ${config.extensionPoint} extension found for module: ${parsed.source}`);
      return undefined;
    }

    if (config.hasDisplayMethods && !config.hasDisplayMethods(extension)) {
      console.warn(`${prefix} Extension for module ${parsed.source} does not implement display methods`);
      return undefined;
    }

    return extension as TExtension;
  };

  return {
    getOne,
    getModuleData: (entity: TEntity): Record<string, any> | null => {
      const parsed = config.parseContext(entity);
      return parsed?.data ?? null;
    },
    isFromModule: (entity: TEntity, moduleId: string): boolean => {
      const parsed = config.parseContext(entity);
      return parsed?.source === moduleId;
    },
    getDisplayExtensions: (entity: TEntity): TExtension[] => {
      const ext = getOne(entity);
      return ext ? [ext] : [];
    },

    /**
     * Get all extensions for this extension point (all enabled modules).
     * Use for merging contributions from multiple modules (e.g. detail sections, actions).
     * Returns { moduleId, extension } so consumers can create unique keys when merging.
     */
    getAll: (entity: TEntity): Array<{ moduleId: string; extension: TExtension }> => {
      const clientId = config.getClientId(entity);
      const pairs = moduleRegistry.getExtensionsForPoint<TExtension>(
        config.extensionPoint,
        clientId
      );
      return pairs.filter(({ extension }) =>
        !config.hasDisplayMethods || config.hasDisplayMethods(extension)
      );
    }
  };
}
